import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import { ToastProvider } from "./components/ui/Toast";
import { useAuthStore } from "./stores/authStore";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DomainListPage from "./pages/DomainListPage";
import DomainDocumentsPage from "./pages/DomainDocumentsPage";
import DomainChatPage from "./pages/DomainChatPage";
import DomainExplorePage from "./pages/DomainExplorePage";
import DocumentViewPage from "./pages/DocumentViewPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminRbacPage from "./pages/AdminRbacPage";

function AuthInitializer({ children }) {
  const { isLoading, init, error: authError } = useAuthStore();
  const [isInit, setIsInit] = useState(false);
  
  useEffect(() => {
    let cancelled = false;
    
    async function initialize() {
      try {
        await init();
      } catch (err) {
        // Ignore SSO init errors so login page remains usable
        console.warn('[AuthInitializer] init error:', err);
      } finally {
        if (!cancelled) setIsInit(true);
      }
    }
    
    initialize();
    
    return () => {
      cancelled = true;
    };
  }, [init]);
  
  if (isLoading || !isInit) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return children;
}

function ProtectedRoute({ children }) {
  const { user, isAuthenticated } = useAuthStore();
  const location = useLocation();
  
  // Allow if authenticated
  if (isAuthenticated || user) {
    return children;
  }
  
  // Otherwise redirect to login
  return <Navigate to="/login" state={{ from: location }} replace />;
}

function PublicRoute({ children }) {
  const { user, isAuthenticated } = useAuthStore();
  if (user || isAuthenticated) {
    return <Navigate to="/domains" replace />;
  }
  return children;
}

function AppLayout() {
  const { pathname } = useLocation();
  const isAuthPage = pathname === "/login" || pathname === "/register";

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {!isAuthPage && <Sidebar />}
      <main className={isAuthPage ? "" : "ml-64 min-h-screen"}>
        <div className="p-6">
          <Routes>
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/" element={<ProtectedRoute><Navigate to="/domains" replace /></ProtectedRoute>} />
            <Route path="/domains" element={<ProtectedRoute><DomainListPage /></ProtectedRoute>} />
            <Route path="/domains/:id/documents" element={<ProtectedRoute><DomainDocumentsPage /></ProtectedRoute>} />
            <Route path="/domains/:id/chat" element={<ProtectedRoute><DomainChatPage /></ProtectedRoute>} />
            <Route path="/domains/:id/explore" element={<ProtectedRoute><DomainExplorePage /></ProtectedRoute>} />
            <Route path="/domains/:id/documents/:docId" element={<ProtectedRoute><DocumentViewPage /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute><AdminUsersPage /></ProtectedRoute>} />
            <Route path="/admin/rbac" element={<ProtectedRoute><AdminRbacPage /></ProtectedRoute>} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

export default function App() {
  useEffect(() => {
    function handleLogout() {
      window.location.href = "/login";
    }
    window.addEventListener("auth:logout", handleLogout);
    return () => window.removeEventListener("auth:logout", handleLogout);
  }, []);

  return (
    <ToastProvider>
      <BrowserRouter>
        <AuthInitializer>
          <AppLayout />
        </AuthInitializer>
      </BrowserRouter>
    </ToastProvider>
  );
}
